import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { guidesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Shield, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Phone, ExternalLink, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const WorkplaceRightsPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(['basic_rights']);

  useEffect(() => {
    fetchGuide();
  }, []);

  const fetchGuide = async () => {
    try {
      const res = await guidesAPI.getWorkplaceRights();
      setGuide(res.data);
    } catch (error) {
      console.error('Failed to fetch guide:', error);
      toast.error('Failed to load guide');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    if (expandedSections.includes(sectionId)) {
      setExpandedSections(expandedSections.filter(id => id !== sectionId));
    } else {
      setExpandedSections([...expandedSections, sectionId]);
    }
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">SA Workplace Rights Guide</h2>
          <p className="text-slate-600 mb-6">Know your rights as an employee. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">SA Workplace Rights Guide</h1>
            <p className="text-sm text-slate-500">Know your rights under South African labour law</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Important Disclaimer</p>
            <p className="text-sm text-amber-700">
              This guide provides general information and is not legal advice. For specific legal matters, 
              consult with a qualified labour law practitioner or contact the CCMA.
            </p>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 lg:p-8 text-white mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{guide?.title || 'Your Workplace Rights'}</h2>
              <p className="text-slate-300">{guide?.subtitle || 'Under South African Labour Law'}</p>
            </div>
          </div>
          
          {guide?.key_laws && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {guide.key_laws.map((law, i) => (
                <div key={i} className="bg-white/10 rounded-lg p-3">
                  <p className="font-medium">{law.name}</p>
                  <p className="text-sm text-slate-300">{law.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {guide?.sections?.map((section, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                data-testid={`section-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    section.type === 'warning' ? 'bg-red-100' :
                    section.type === 'info' ? 'bg-blue-100' :
                    'bg-emerald-100'
                  }`}>
                    <BookOpen className={`w-5 h-5 ${
                      section.type === 'warning' ? 'text-red-600' :
                      section.type === 'info' ? 'text-blue-600' :
                      'text-emerald-600'
                    }`} />
                  </div>
                  <span className="font-semibold text-slate-900">{section.title}</span>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {expandedSections.includes(section.id) && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  {section.summary && (
                    <p className="text-slate-600 mb-4">{section.summary}</p>
                  )}

                  {section.content && (
                    <ul className="space-y-3">
                      {section.content.map((item, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.warnings && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="font-medium text-red-800 mb-2">Important Notes:</p>
                      <ul className="space-y-1 text-sm text-red-700">
                        {section.warnings.map((warning, k) => (
                          <li key={k}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.examples && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="font-medium text-blue-800 mb-2">Examples:</p>
                      <ul className="space-y-1 text-sm text-blue-700">
                        {section.examples.map((example, k) => (
                          <li key={k}>• {example}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Helpful Contacts */}
        {guide?.contacts && (
          <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Helpful Contacts
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {guide.contacts.map((contact, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-4">
                  <p className="font-semibold text-slate-900">{contact.name}</p>
                  <p className="text-sm text-slate-600 mb-2">{contact.description}</p>
                  {contact.phone && (
                    <p className="text-sm">
                      <span className="text-slate-500">Tel:</span>{' '}
                      <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">
                        {contact.phone}
                      </a>
                    </p>
                  )}
                  {contact.website && (
                    <a
                      href={contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                    >
                      Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CCMA Info */}
        <div className="mt-6 bg-blue-600 rounded-2xl p-6 text-white">
          <h3 className="text-lg font-bold mb-2">Need Help?</h3>
          <p className="text-blue-100 mb-4">
            If you believe your rights have been violated, you can file a complaint with the CCMA 
            (Commission for Conciliation, Mediation and Arbitration) within 30 days of the incident.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://www.ccma.org.za"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Visit CCMA Website
            </a>
            <a
              href="tel:0861168168"
              className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call: 0861 16 16 16
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WorkplaceRightsPage;
